import { AppImage } from '@/components/ui/app-image';
import { Text } from '@/components/ui/text';
import { useAppTheme } from '@/providers/theme-provider';
import { locationMapUrl } from '@/utils/location-map-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useColorScheme } from 'nativewind';
import { useState } from 'react';
import { Linking, Pressable, View } from 'react-native';

const MAP_HEIGHT = 200;
const PIN_SIZE = 36;
const FADE_MS = 150;
const GEOAPIFY_URL = 'https://www.geoapify.com/';
const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright';

interface LocationMapProps {
  businessId: string;
  businessName: string;
  /** Business updatedAt; busts the client image cache after an edit. */
  version: number;
  onPress: () => void;
}

/**
 * The Location map (ADR 0009): the gateway's unmarked warm basemap with the
 * pin drawn here at dead center, plus the attribution the provider requires
 * (the gateway trims Geoapify's own stamp). Hides itself if the image fails,
 * leaving the address card and button to do the job.
 *
 * Parents should key it by business id so a failure does not stick across
 * businesses.
 */
export function LocationMap({ businessId, businessName, version, onPress }: LocationMapProps) {
  // Resolved scheme (the UI is forced dark), not the stored preference.
  const { colorScheme } = useColorScheme();
  const { colors } = useAppTheme();
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  const uri = locationMapUrl(businessId, colorScheme === 'light' ? 'light' : 'dark', version);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Open ${businessName} in Maps`}
      className="bg-surface relative overflow-hidden"
      style={{ height: MAP_HEIGHT }}
    >
      <AppImage
        source={{ uri }}
        decorative
        contentFit="cover"
        transition={FADE_MS}
        onError={() => setFailed(true)}
        style={{ width: '100%', height: MAP_HEIGHT }}
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginLeft: -PIN_SIZE / 2,
          marginTop: -PIN_SIZE,
        }}
      >
        <Ionicons name="location" size={PIN_SIZE} color={colors.primary} />
      </View>
      <Text
        className="absolute bottom-1 right-2 text-[10px] text-muted-foreground"
        accessibilityRole="text"
      >
        <Text onPress={() => void Linking.openURL(GEOAPIFY_URL)}>© Geoapify</Text>
        {' '}
        <Text>© OpenMapTiles</Text>
        {' '}
        <Text onPress={() => void Linking.openURL(OSM_COPYRIGHT_URL)}>© OpenStreetMap</Text>
      </Text>
    </Pressable>
  );
}
